/**
 * Report Generation Route (Stories 5.3, 5.4, 5.8, 5.10)
 *
 * Route: /reports/generate
 * Search params: ?clientId=<id>&templateId=<id>
 *
 * Flow:
 * 1. No clientId → show template gallery with client selector popover
 * 2. clientId present, no templateId → show template gallery (interactive)
 * 3. templateId present → show canvas layout editor (Story 5.4)
 * 4. Canvas complete → date range selection (Story 5.8)
 * 5. Date range complete → preview (Story 5.10)
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass, Users, Check, CaretDown } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@provenleap/ui";
import { apiGet, apiPost } from "../../../lib/apiClient";
import { useReportTemplates } from "../../../hooks/useReportTemplates";
import { useClientIntegrations } from "../../../hooks/useClientIntegrations";
import { useUndoStack } from "../../../hooks/useUndoStack";
import { TemplateGallery } from "../../../components/reports/TemplateGallery";
import { TemplatePreviewPanel } from "../../../components/reports/TemplatePreviewPanel";
import { SmartReportCard } from "../../../components/reports/SmartReportCard";
import { McpReportCard } from "../../../components/reports/McpReportCard";
import { StartFromScratchCard } from "../../../components/reports/StartFromScratchCard";
import { CompactDateRangeBar } from "../../../components/reports/CompactDateRangeBar";
import { PortalLightTooltip } from "../../../components/reports/PortalLightTooltip";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import {
  getRequiredPlatforms,
  getMissingPlatforms,
  filterWidgetsByAvailablePlatforms,
} from "../../../lib/templateUtils";
import { PICKER_PLATFORMS, logoUrl } from "../../../lib/integrations";
import type { WidgetConfig } from "../../../lib/templateUtils";
import { useDateRange } from "../../../hooks/useDateRange";

import { ReportPreviewPanel } from "../../../components/reports/ReportPreviewPanel";
import { ReportPreviewModal } from "../../../components/reports/ReportPreviewModal";
import { useDebounce } from "../../../hooks/useDebounce";
import { useReportData } from "../../../hooks/useReportData";
import { useRollupData } from "../../../hooks/useRollupData";
import { useBrand } from "../../../hooks/useBrands";
import { RollupSetupStep } from "../../../components/reports/RollupSetupStep";
import { ROLLUP_MEMBER_CAP } from "../../../components/reports/rollupPaletteGate";
import { useToast } from "../../../hooks/useToast";
import { useGeneratePdf } from "../../../hooks/useGeneratePdf";
import { Toast } from "../../../components/clients/Toast";
import { ClientAvatar } from "../../../components/clients/ClientAvatar";
import { PdfGenerationModal } from "../../../components/reports/PdfGenerationModal";
import { FormatSelectionStep } from "../../../components/reports/FormatSelectionStep";
import { createLoadingEnvelope } from "../../../lib/widgetData";

export interface GenerateSearchParams {
  clientId?: string;
  templateId?: string;
  format?: "document" | "slide-deck";
  // Story 40-6 — roll-up wizard state, carried deep-link-safe in the URL.
  // `kind` ("client"|"rollup") gates the whole roll-up branch; absent ⇒ the
  // existing single-client flow (byte-identical). `rollupScope` + the member set
  // (`brandId` for brand scope, comma-joined `members` for portfolio) are only
  // meaningful for a roll-up.
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio";
  brandId?: string;
  /** Comma-joined portfolio member client ids (bounded ≤ ROLLUP_MEMBER_CAP). */
  members?: string;
}

export const Route = createFileRoute("/_authenticated/reports/generate")({
  validateSearch: (search: Record<string, unknown>): GenerateSearchParams => ({
    clientId: typeof search.clientId === "string" ? search.clientId : undefined,
    templateId:
      typeof search.templateId === "string" ? search.templateId : undefined,
    format:
      search.format === "document" || search.format === "slide-deck"
        ? search.format
        : undefined,
    kind:
      search.kind === "client" || search.kind === "rollup"
        ? search.kind
        : undefined,
    rollupScope:
      search.rollupScope === "brand" || search.rollupScope === "portfolio"
        ? search.rollupScope
        : undefined,
    brandId: typeof search.brandId === "string" ? search.brandId : undefined,
    members: typeof search.members === "string" ? search.members : undefined,
  }),
  component: GenerateReportPage,
});

/** Parse the comma-joined portfolio member param into a bounded, de-duplicated
 *  id list (the create route re-validates in-tenant — this is purely UX). */
function parseMemberIds(members: string | undefined): string[] {
  if (!members) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of members.split(",")) {
    const trimmed = id.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      out.push(trimmed);
    }
  }
  return out.slice(0, ROLLUP_MEMBER_CAP);
}

interface Client {
  id: string;
  name: string;
  logoUrl: string | null;
  isArchived: number;
  platforms?: string[];
}

// ── Constants ────────────────────────────────────────────────────────

const FULL_MARKETING_TEMPLATE_ID = "00000000-0000-0000-0000-template00006";

// ── Step type for report generation flow ────────────────────────────────

// Story 40-6 — a new "type" step (Client vs Roll-up; then Brand vs Portfolio)
// is inserted BEFORE "format". The single-client path runs the existing
// type→format→template→canvas→preview machine unchanged.
type ReportStep = "type" | "format" | "template" | "canvas" | "preview";

// ── Client Selector Popover ──────────────────────────────────────────

function ClientSelectorPopover({
  selectedClientId,
  selectedClient,
  onSelectClient,
}: {
  selectedClientId?: string;
  selectedClient?: { id: string; name: string; logoUrl: string | null } | null;
  onSelectClient: (clientId: string) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");

  // When Radix opens, show content immediately. When closing, delay Radix
  // close until framer-motion exit animation finishes.
  const handleOpenChange = (v: boolean) => {
    if (v) {
      setOpen(true);
      setVisible(true);
    } else {
      setVisible(false);
      // Radix stays open until exit animation completes (see onAnimationComplete)
    }
  };
  const handleExitComplete = () => {
    if (!visible) {
      setOpen(false);
      setSearch("");
    }
  };

  const {
    data: clientsData,
    isLoading,
    error,
    refetch,
  } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to fetch clients");
      const json = await res.json();
      // Cache the FULL list (shared ["clients"] cache); filter archived locally
      // so we don't overwrite the Clients page's cache with a row subset.
      return json.data as Client[];
    },
  });

  const clients = (clientsData ?? []).filter((c) => !c.isArchived);
  const filtered = search
    ? clients.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      )
    : clients;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-2.5 rounded-2xl h-11 px-5 min-w-[400px] text-sm bg-card hover:bg-card hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-pointer shadow-xs"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="client-selector-trigger"
        >
          {selectedClient ? (
            <>
              <ClientAvatar name={selectedClient.name} logoUrl={selectedClient.logoUrl ?? null} size="sm" />
              <span className="flex-1 text-left font-normal text-[14px] text-foreground">{selectedClient.name}</span>
            </>
          ) : (
            <span className="flex-1 text-left font-normal text-[14px] text-foreground/75">Select a client</span>
          )}
          <CaretDown size={14} className="text-muted-foreground ml-auto" />
        </button>
      </PopoverTrigger>
      <AnimatePresence onExitComplete={handleExitComplete}>
        {visible && (
          <PopoverContent className="w-72 p-2 overflow-visible rounded-2xl shadow-md bg-popover relative" align="start" sideOffset={10} forceMount asChild style={{ border: "0.5px solid var(--hairline)" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
        {/* Arrow pointing up */}
        <div
          className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
          style={{ borderLeft: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" }}
        />

        {/* Search */}
        <div className="px-2 pt-2 pb-0.5">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
              style={{ outline: "none", boxShadow: "none" }}
              data-testid="client-selector-search"
              autoFocus
            />
          </div>
        </div>

        {/* Client list */}
        <div className="max-h-[260px] overflow-y-auto overflow-x-hidden pt-2 pb-1" data-testid="client-selector-list">
          {isLoading && (
            <div className="p-3 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 px-2 py-1.5 animate-pulse">
                  <div className="w-7 h-7 rounded-full bg-muted" />
                  <div className="h-3.5 bg-muted rounded w-24" />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="p-4 text-center" data-testid="client-selector-error">
              <p className="text-sm text-destructive mb-2">Failed to load clients</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !error && clients.length === 0 && (
            <div className="p-4 text-center" data-testid="client-selector-empty">
              <p className="text-sm text-muted-foreground mb-3">No clients yet</p>
              <Button
                variant="brand"
                size="sm"
                onClick={() => { setOpen(false); navigate({ to: "/clients" }); }}
              >
                <Users size={14} className="mr-1" />
                Create Your First Client
              </Button>
            </div>
          )}

          {!isLoading && !error && filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => { onSelectClient(c.id); setVisible(false); }}
              className="flex items-stretch gap-2.5 w-[calc(100%-12px)] ml-1 rounded-lg px-2 py-2 text-[13px] text-left transition-colors cursor-pointer border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs"
              data-testid={`client-option-${c.id}`}
            >
              <ClientAvatar name={c.name} logoUrl={c.logoUrl ?? null} size="sm" />
              <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <span className="truncate">{c.name}</span>
                {c.platforms && c.platforms.length > 0 && (
                  <div className="flex items-center flex-nowrap gap-1">
                    {c.platforms.slice(0, 7).map((platformId) => {
                      const platform = PICKER_PLATFORMS.find((p) => p.platform === platformId);
                      if (!platform?.domain) return null;
                      return (
                        <PortalLightTooltip key={platformId} label={platform.displayName}>
                          <img
                            src={logoUrl(platform.domain)}
                            alt={platform.displayName}
                            className="w-5 h-5 rounded-full bg-white ring-[1.5px] ring-white object-contain cursor-pointer"
                            loading="lazy"
                          />
                        </PortalLightTooltip>
                      );
                    })}
                    {c.platforms.length > 7 && (() => {
                      const hidden = c.platforms.slice(7)
                        .map((pid) => PICKER_PLATFORMS.find((p) => p.platform === pid)?.displayName)
                        .filter((n): n is string => Boolean(n))
                        .join(", ");
                      return (
                        <PortalLightTooltip label={hidden}>
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-background text-[10px] font-medium text-foreground cursor-pointer border border-input shadow-xs">
                            +{c.platforms.length - 7}
                          </span>
                        </PortalLightTooltip>
                      );
                    })()}
                  </div>
                )}
              </div>
              {c.id === selectedClientId && (
                <div className="self-start mr-[-3px] w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <Check size={12} className="text-white" weight="bold" />
                </div>
              )}
            </button>
          ))}

          {!isLoading && !error && clients.length > 0 && filtered.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground text-center">No clients match your search</p>
          )}
        </div>
            </motion.div>
          </PopoverContent>
        )}
      </AnimatePresence>
    </Popover>
  );
}

// ── Missing Integrations Dialog ────────────────────────────────────────

function MissingIntegrationsBanner({
  visible,
  missingPlatforms,
  allMissing,
  clientId,
  onContinue,
  onDismiss,
}: {
  visible: boolean;
  missingPlatforms: string[];
  allMissing: boolean;
  clientId: string;
  onContinue: () => void;
  onDismiss: () => void;
}) {
  const navigate = useNavigate();

  if (!visible) return null;

  const platformNames = missingPlatforms.map((id) => {
    const p = PICKER_PLATFORMS.find((pp) => pp.platform === id);
    return p?.displayName ?? id;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
      data-testid="missing-integrations-banner"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-amber-600">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-amber-800" data-testid="banner-message">
            {allMissing
              ? `This template requires data from ${platformNames.join(", ")}. Connect them to use this template with full data.`
              : `Some integrations are missing: ${platformNames.join(", ")}. The report will show partial data.`}
          </p>
          <div className="flex gap-2 mt-2">
            {!allMissing && (
              <Button
                variant="outline"
                size="sm"
                onClick={onContinue}
                data-testid="banner-continue"
              >
                Continue with Partial Data
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                onDismiss();
                navigate({
                  to: "/clients/$clientId",
                  params: { clientId },
                  hash: "integrations",
                });
              }}
              data-testid="banner-connect"
            >
              Connect Integrations
            </Button>
          </div>
        </div>
        <button
          className="shrink-0 text-amber-500 hover:text-amber-700 transition-colors"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="banner-dismiss"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </motion.div>
  );
}


// ── Main Page ──────────────────────────────────────────────────────────
// NOTE: CanvasLayoutEditor was extracted to CanvasEditor.tsx (Story 5.31)
// DEFAULT_WIDGET_TITLES and DEFAULT_WIDGET_CONFIGS now live in CanvasEditor.tsx

function GenerateReportPage() {
  const {
    clientId,
    templateId,
    format: formatParam,
    kind: kindParam,
    rollupScope: rollupScopeParam,
    brandId: brandIdParam,
    members: membersParam,
  } = Route.useSearch();
  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState<"document" | "slide-deck">(
    formatParam ?? "document"
  );
  // Story 40-6 — roll-up vs single-client kind (search-param driven).
  const isRollup = kindParam === "rollup";
  const portfolioMemberIds = useMemo(
    () => parseMemberIds(membersParam),
    [membersParam],
  );
  // Initial step: a deep-link with a template/format jumps straight in (old
  // behavior preserved); an old `?clientId=` link (no kind) is an in-progress
  // CLIENT flow and lands on format; otherwise start at the new "type" step.
  const [step, setStep] = useState<ReportStep>(
    templateId
      ? "canvas"
      : formatParam
        ? "template"
        : kindParam || clientId
          ? "format"
          : "type"
  );
  const [isExiting, setIsExiting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<"scratch" | "smart" | "mcp" | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedReportId, setSavedReportId] = useState<string | null>(null);
  const { toast, showToast } = useToast();
  const pdf = useGeneratePdf();
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    missingPlatforms: string[];
    allMissing: boolean;
    pendingTemplateId: string | null;
  }>({
    open: false,
    missingPlatforms: [],
    allMissing: false,
    pendingTemplateId: null,
  });

  // Format-switch confirmation modal state (Story 5.23, AC: #10)
  const [formatSwitchModal, setFormatSwitchModal] = useState<{
    open: boolean;
    pendingFormat: "document" | "slide-deck" | null;
  }>({ open: false, pendingFormat: null });

  const { templates, isLoading: templatesLoading } = useReportTemplates();
  const { connectedPlatforms, isLoading: integrationsLoading } =
    useClientIntegrations(clientId);
  const dateRange = useDateRange();

  // Template preview panel state (Phase 2)
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);

  // Reset templates expanded state when client changes
  const prevClientId = useRef(clientId);
  if (prevClientId.current !== clientId) {
    prevClientId.current = clientId;
    setTemplatesExpanded(false);
  }
  const previewTemplate = useMemo(
    () => (previewTemplateId ? templates.find((t) => t.id === previewTemplateId) ?? null : null),
    [previewTemplateId, templates]
  );

  // Filter templates by selected format (Story 5.23, Task 4)
  const filteredTemplates = useMemo(
    () => templates.filter((t) => (t.format ?? "document") === selectedFormat),
    [templates, selectedFormat]
  );

  // Resolve selected template (moved up for state lifting, Story 5.10)
  const selectedTemplate = templateId
    ? templates.find((t) => t.id === templateId)
    : null;

  // Lifted layout state from CanvasLayoutEditor (Story 5.10, Task 1)
  // Undo history persists across step navigation (canvas ↔ dateRange ↔ preview)
  const initialWidgets = useMemo(
    () => selectedTemplate?.parsedLayout.widgets ?? [],
    [selectedTemplate]
  );
  const {
    state: layoutWidgets,
    reset: resetUndoStack,
  } = useUndoStack<WidgetConfig[]>(initialWidgets);

  // Reset undo stack when template changes OR becomes available after async load.
  // Uses selectedTemplate.id (not URL templateId) so it fires when templates
  // finish loading and selectedTemplate transitions from null → resolved.
  const lastResolvedTemplateIdRef = useRef<string | undefined>(undefined);
  const smartReportWidgetsRef = useRef<WidgetConfig[] | null>(null);
  const resolvedId = selectedTemplate?.id;
  if (resolvedId && resolvedId !== lastResolvedTemplateIdRef.current) {
    lastResolvedTemplateIdRef.current = resolvedId;
    // Smart report: use filtered widgets if available, otherwise full template
    const initialWidgetsForCanvas = smartReportWidgetsRef.current ?? selectedTemplate.parsedLayout.widgets;
    smartReportWidgetsRef.current = null; // Clear after use
    resetUndoStack(initialWidgetsForCanvas);
  }

  // Debounced preview data for the preview step (Story 5.10)
  // Hooks always run (React rules) but useMemo short-circuits on non-preview steps
  const debouncedWidgets = useDebounce(layoutWidgets, 500);
  const isDebouncing = layoutWidgets !== debouncedWidgets;

  // Real data from integration APIs (Story 5.11) — only active during preview step
  const comparisonDates =
    dateRange.state.comparisonEnabled &&
    dateRange.state.comparisonStart &&
    dateRange.state.comparisonEnd
      ? {
          start: dateRange.state.comparisonStart,
          end: dateRange.state.comparisonEnd,
        }
      : null;

  // ── Roll-up member resolution (Story 40-6, AC2/AC5) ──────────────────
  // Brand members are resolved DYNAMICALLY (never frozen) from the brand's
  // current clients; portfolio members come from the URL member set. The query
  // is idle for a single-client report (undefined brandId). The member-union
  // `connectedPlatforms` is the union of each member's `platforms` — no extra
  // fetch (each member carries `platforms` on the brand-detail / clients data).
  const { data: rollupBrand } = useBrand(
    isRollup && rollupScopeParam === "brand" ? brandIdParam : undefined,
  );
  const rollupMemberIds = useMemo<string[]>(() => {
    if (!isRollup) return [];
    if (rollupScopeParam === "brand") {
      return (rollupBrand?.clients ?? []).map((c) => c.id);
    }
    return portfolioMemberIds;
  }, [isRollup, rollupScopeParam, rollupBrand, portfolioMemberIds]);

  // (The member-union `connectedPlatforms` is computed in the EDITOR route,
  // where the CanvasEditor consumes it; the generate-step preview renders
  // ReportPreviewPanel, which doesn't need it.)

  // Single-client preview data (existing path) — idle for a roll-up preview.
  const singleClientPreview = useReportData(
    !isRollup && step === "preview" ? clientId : undefined,
    layoutWidgets,
    { start: dateRange.state.start, end: dateRange.state.end },
    comparisonDates
  );
  // Roll-up preview data (Story 40-6) — idle for a single-client preview.
  const rollupPreview = useRollupData(
    isRollup && step === "preview" ? rollupMemberIds : [],
    layoutWidgets,
    { start: dateRange.state.start, end: dateRange.state.end },
    comparisonDates
  );
  const {
    widgetData: realWidgetData,
    platformStatus,
    isLoading: dataLoading,
    hasStaleComparison,
  } = isRollup ? rollupPreview : singleClientPreview;

  // For the full-screen modal, use real data when in preview step, else mock
  const previewData = useMemo(() => {
    if (step !== "preview") return {};
    if (isDebouncing) {
      const loadingMap: Record<string, import("../../../lib/widgetData").WidgetData> = {};
      for (const widget of layoutWidgets) {
        loadingMap[widget.id] = createLoadingEnvelope();
      }
      return loadingMap;
    }
    return realWidgetData;
  }, [step, isDebouncing, layoutWidgets, realWidgetData]);

  // Fetch client info for the header
  const { data: client } = useQuery<{
    id: string;
    name: string;
    logoUrl: string | null;
    industry: string | null;
    businessGoals: string | null;
    notes: string | null;
  }>({
    queryKey: ["clients", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}`);
      if (!res.ok) throw new Error("Failed to load client");
      const json = await res.json();
      return json.data;
    },
    enabled: !!clientId,
  });

  // The report's identity is "ready" once a client is chosen (single-client) OR
  // a non-empty member set is resolved (roll-up). Used to gate the generate-method
  // cards + template selection for BOTH kinds.
  const reportIdentityReady = isRollup ? rollupMemberIds.length > 0 : !!clientId;

  // Build the editor-route search that carries the report identity into the
  // editor. SINGLE-CLIENT keeps `{ clientId, templateId?, format }` byte-identical;
  // a ROLL-UP carries `kind`/`rollupScope` + the member set so the editor (for a
  // NEW, not-yet-saved report) can resolve members without a server round-trip.
  const buildEditorSearch = useCallback(
    (tid?: string) => {
      const base = { format: selectedFormat, ...(tid ? { templateId: tid } : {}) };
      if (isRollup) {
        return {
          ...base,
          kind: "rollup" as const,
          ...(rollupScopeParam ? { rollupScope: rollupScopeParam } : {}),
          ...(rollupScopeParam === "brand"
            ? { brandId: brandIdParam }
            : membersParam
              ? { members: membersParam }
              : {}),
        };
      }
      return { ...base, clientId };
    },
    [isRollup, rollupScopeParam, brandIdParam, membersParam, clientId, selectedFormat],
  );

  const confirmSelection = useCallback((tid: string) => {
    setIsGenerating(true);
    setTimeout(() => {
      navigate({
        to: "/reports/$reportId/edit",
        params: { reportId: "new" },
        search: buildEditorSearch(tid),
      });
    }, 1500);
  }, [navigate, buildEditorSearch]);

  const handleSelectTemplate = (tid: string) => {
    if (!reportIdentityReady) return;
    const template = templates.find((t) => t.id === tid);
    if (!template) return;

    // Roll-ups don't have a single client's connected set to check against the
    // template's required platforms (members span many clients) — skip the
    // missing-integrations dialog and go straight to the editor.
    if (isRollup) {
      confirmSelection(tid);
      return;
    }

    const requiredPlatforms = getRequiredPlatforms(template.parsedLayout);
    const missing = getMissingPlatforms(requiredPlatforms, connectedPlatforms);

    if (missing.length === 0) {
      confirmSelection(tid);
      return;
    }

    const allMissing = missing.length === requiredPlatforms.length;
    setDialogState({
      open: true,
      missingPlatforms: missing,
      allMissing,
      pendingTemplateId: tid,
    });
  };

  // Smart report: use Full Marketing template, filtered to connected platforms
  const handleSmartReport = useCallback(() => {
    if (!clientId) return;
    const fullMarketing = templates.find((t) => t.id === FULL_MARKETING_TEMPLATE_ID);
    if (!fullMarketing) return;
    // Store filtered widgets for use when the canvas loads
    smartReportWidgetsRef.current = filterWidgetsByAvailablePlatforms(
      fullMarketing.parsedLayout.widgets,
      connectedPlatforms
    );
    confirmSelection(FULL_MARKETING_TEMPLATE_ID);
  }, [clientId, templates, connectedPlatforms, confirmSelection]);

  const handleStartFromScratch = useCallback(() => {
    if (!reportIdentityReady) return;
    setIsGenerating(true);
    setTimeout(() => {
      navigate({
        to: "/reports/$reportId/edit",
        params: { reportId: "new" },
        search: buildEditorSearch(),
      });
    }, 600);
  }, [reportIdentityReady, navigate, buildEditorSearch]);

  const handleDeselect = () => {
    navigate({
      to: "/reports/generate",
      search: buildWizardSearch(selectedFormat),
    });
  };

  // Build the search object that carries the report's identity through the
  // wizard. SINGLE-CLIENT keeps `{ clientId, format }` byte-identical to today; a
  // ROLL-UP carries `kind`/`rollupScope` + the member set (brandId | members).
  const buildWizardSearch = useCallback(
    (format: "document" | "slide-deck"): GenerateSearchParams => {
      if (isRollup) {
        return {
          kind: "rollup",
          ...(rollupScopeParam ? { rollupScope: rollupScopeParam } : {}),
          ...(rollupScopeParam === "brand"
            ? { brandId: brandIdParam }
            : membersParam
              ? { members: membersParam }
              : {}),
          format,
        };
      }
      return { clientId, format };
    },
    [isRollup, rollupScopeParam, brandIdParam, membersParam, clientId],
  );

  // Format selection handler (Story 5.23, AC: #10)
  const handleFormatSelect = useCallback((format: "document" | "slide-deck") => {
    // If user has already built a canvas and is switching format, confirm first
    if (layoutWidgets.length > 0 && format !== selectedFormat) {
      setFormatSwitchModal({ open: true, pendingFormat: format });
      return;
    }
    setSelectedFormat(format);
    navigate({
      to: "/reports/generate",
      search: buildWizardSearch(format),
    });
    setStep("template");
  }, [selectedFormat, layoutWidgets.length, navigate, buildWizardSearch]);

  const handleFormatSwitchConfirm = useCallback(() => {
    const newFormat = formatSwitchModal.pendingFormat;
    setFormatSwitchModal({ open: false, pendingFormat: null });
    if (newFormat) {
      setSelectedFormat(newFormat);
      navigate({
        to: "/reports/generate",
        search: buildWizardSearch(newFormat),
      });
      setStep("template");
    }
  }, [formatSwitchModal.pendingFormat, navigate, buildWizardSearch]);

  // ── Type-picker (Story 40-6) — kind / scope / member selection ─────────
  // Each selection is persisted in the URL search params (deep-link safe). The
  // "Continue" affordance is gated by `canContinueFromType` below.
  const handleSelectKind = useCallback(
    (k: "client" | "rollup") => {
      navigate({ to: "/reports/generate", search: { kind: k } });
    },
    [navigate],
  );
  const handleSelectScope = useCallback(
    (scope: "brand" | "portfolio") => {
      navigate({ to: "/reports/generate", search: { kind: "rollup", rollupScope: scope } });
    },
    [navigate],
  );
  const handleSelectBrand = useCallback(
    (bId: string) => {
      navigate({
        to: "/reports/generate",
        search: { kind: "rollup", rollupScope: "brand", brandId: bId },
      });
    },
    [navigate],
  );
  const handlePortfolioChange = useCallback(
    (ids: string[]) => {
      navigate({
        to: "/reports/generate",
        search: {
          kind: "rollup",
          rollupScope: "portfolio",
          ...(ids.length > 0 ? { members: ids.join(",") } : {}),
        },
      });
    },
    [navigate],
  );

  // Whether the user can advance from the type step to format. A CLIENT report
  // advances immediately (the client selector lives in the template step, as
  // before). A ROLL-UP needs a non-empty resolved member set (brand with ≥1
  // member, or ≥1 portfolio client).
  const canContinueFromType = isRollup
    ? rollupMemberIds.length > 0 && rollupMemberIds.length <= ROLLUP_MEMBER_CAP
    : kindParam === "client";

  const handleContinueFromType = useCallback(() => {
    if (!canContinueFromType) return;
    navigate({
      to: "/reports/generate",
      search: buildWizardSearch(selectedFormat),
    });
    setStep("format");
  }, [canContinueFromType, navigate, buildWizardSearch, selectedFormat]);

  const handleBackToCanvas = useCallback(() => {
    setStep("canvas");
    setValidationError(null);
  }, []);

  // Save report with data snapshot (Story 5.11, Task 7; Story 40-6 roll-up).
  // A SINGLE-CLIENT save requires a clientId + template (byte-identical payload).
  // A ROLL-UP save carries NO clientId — instead kind/rollupScope/brandId or the
  // member set; the server discriminator (40-6 Task 1) validates the shape.
  const handleSaveReport = useCallback(async () => {
    if (isSaving) return;
    if (!isRollup && (!clientId || !selectedTemplate)) return;
    if (isRollup && rollupMemberIds.length === 0) return;

    setIsSaving(true);
    try {
      const layoutJson = JSON.stringify({
        version: 1,
        format: selectedFormat,
        grid: selectedTemplate?.parsedLayout.grid ?? { columns: 12 },
        widgets: layoutWidgets.map((w) => ({
          id: w.id,
          type: w.type,
          title: w.title,
          position: w.position,
          config: w.config,
        })),
      });

      const dataSnapshotJson = JSON.stringify(
        isRollup
          ? { ...realWidgetData }
          : {
              ...realWidgetData,
              _clientContext: {
                industry: client?.industry ?? null,
                businessGoals: client?.businessGoals ?? null,
                notes: client?.notes ?? null,
              },
            }
      );

      // Roll-up name reflects the brand / portfolio; single-client reflects the
      // template name (unchanged). Both carry the date range.
      const rollupName =
        rollupScopeParam === "brand"
          ? `${rollupBrand?.name ?? "Brand"} roll-up`
          : `Portfolio roll-up (${rollupMemberIds.length} clients)`;
      const name = isRollup
        ? `${rollupName} - ${dateRange.state.start} to ${dateRange.state.end}`
        : `${selectedTemplate!.name} - ${dateRange.state.start} to ${dateRange.state.end}`;

      // Roll-up payload: NO clientId; kind + scope + member set. Single-client
      // payload is byte-identical to today (no kind field).
      const rollupPayload = isRollup
        ? {
            kind: "rollup" as const,
            rollupScope: rollupScopeParam,
            ...(rollupScopeParam === "brand"
              ? { brandId: brandIdParam }
              : { memberClientIds: rollupMemberIds }),
          }
        : { clientId, templateId };

      const res = await apiPost("/api/reports", {
        ...rollupPayload,
        name,
        dateRange: { start: dateRange.state.start, end: dateRange.state.end },
        ...(comparisonDates ? { comparison: comparisonDates } : {}),
        layoutJson,
        dataSnapshotJson,
        format: selectedFormat,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail ?? err?.title ?? "Failed to save report");
      }

      const json = await res.json().catch(() => null) as { data?: { id?: string } } | null;
      if (json?.data?.id) {
        setSavedReportId(json.data.id);
      }

      showToast("success", "Report saved successfully");
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Failed to save report");
    } finally {
      setIsSaving(false);
    }
  }, [clientId, selectedTemplate, isSaving, isRollup, rollupScopeParam, brandIdParam, rollupMemberIds, rollupBrand, layoutWidgets, realWidgetData, client, dateRange.state, comparisonDates, templateId, selectedFormat, showToast]);

  // PDF generation handlers
  const handleGeneratePdf = useCallback(() => {
    if (!savedReportId) return;
    setShowPdfModal(true);
    pdf.generatePdf(savedReportId);
  }, [savedReportId, pdf]);

  const handleDownloadPdf = useCallback(() => {
    if (!savedReportId) return;
    pdf.downloadPdf(savedReportId);
  }, [savedReportId, pdf]);

  const handlePdfModalClose = useCallback(() => {
    setShowPdfModal(false);
  }, []);

  const handlePdfRetry = useCallback(() => {
    if (!savedReportId) return;
    pdf.reset();
    pdf.generatePdf(savedReportId);
  }, [savedReportId, pdf]);

  // Sync step when templateId changes
  useEffect(() => {
    if (templateId && (step === "template" || step === "format")) {
      setStep("canvas");
    }
  }, [templateId, step]);

  // Sync selectedFormat when URL param changes (browser back/forward)
  useEffect(() => {
    if (formatParam && formatParam !== selectedFormat) {
      setSelectedFormat(formatParam);
    }
  }, [formatParam]); // intentionally excludes selectedFormat to avoid sync loop

  // ── Compute breadcrumb items + content key per view ──────────────────

  const clientLabel = client ? `New Report for ${client.name}` : "New Report";

  const contentKey =
    step === "type" ? "type" : step === "format" ? "format" : !selectedTemplate ? "template" : step;

  const handleBackToType = useCallback(() => {
    // Back to the type-picker — clears all selections (fresh wizard).
    navigate({ to: "/reports/generate", search: {} });
    setStep("type");
    setSelectedFormat("document");
    setHoveredCard(null);
  }, [navigate]);

  const handleBackToFormat = useCallback(() => {
    // Reset selections — fresh "Choose Format" screen, preserving the chosen
    // kind/scope/members so a roll-up doesn't lose its member set on a back-step.
    navigate({
      to: "/reports/generate",
      search: {
        ...(kindParam ? { kind: kindParam } : {}),
        ...(rollupScopeParam ? { rollupScope: rollupScopeParam } : {}),
        ...(brandIdParam ? { brandId: brandIdParam } : {}),
        ...(membersParam ? { members: membersParam } : {}),
      },
    });
    setStep("format");
    setSelectedFormat("document");
    setHoveredCard(null);
  }, [navigate, kindParam, rollupScopeParam, brandIdParam, membersParam]);

  const breadcrumbItems: import("../../../components/reports/ReportBreadcrumb").BreadcrumbItem[] = [
    { label: "Reports", to: "/reports" },
  ];

  if (step === "type") {
    breadcrumbItems.push({ label: "New Report" });
  } else if (step === "format") {
    breadcrumbItems.push({ label: isRollup ? "Roll-up" : "Client", onClick: handleBackToType });
    breadcrumbItems.push({ label: "Choose Format" });
  } else {
    breadcrumbItems.push({ label: isRollup ? "Roll-up" : "Client", onClick: handleBackToType });
    breadcrumbItems.push({
      label: selectedFormat === "slide-deck" ? "Slide Deck" : "Document",
      onClick: handleBackToFormat,
    });
    breadcrumbItems.push({ label: clientLabel, onClick: selectedTemplate ? handleDeselect : undefined });

    if (selectedTemplate && (step === "canvas" || step === "preview")) {
      breadcrumbItems.push({ label: "Layout", onClick: step !== "canvas" ? handleBackToCanvas : undefined });
    }
    if (selectedTemplate && step === "preview") {
      breadcrumbItems.push({ label: "Preview" });
    }
  }

  const renderStepContent = () => {
    // Type-picker step (Story 40-6) — Client vs Roll-up; then Brand vs Portfolio.
    if (step === "type") {
      return (
        <>
          <RollupSetupStep
            kind={kindParam ?? null}
            rollupScope={rollupScopeParam ?? null}
            brandId={brandIdParam ?? null}
            memberClientIds={portfolioMemberIds}
            onSelectKind={handleSelectKind}
            onSelectScope={handleSelectScope}
            onSelectBrand={handleSelectBrand}
            onPortfolioChange={handlePortfolioChange}
          />
          {kindParam && (
            <div className="max-w-3xl mx-auto mt-8 flex justify-end">
              <Button
                onClick={handleContinueFromType}
                disabled={!canContinueFromType}
                data-testid="rollup-continue-btn"
              >
                Continue
              </Button>
            </div>
          )}
        </>
      );
    }

    // Format selection step (Story 5.23)
    if (step === "format") {
      return (
        <>
          <FormatSelectionStep
            defaultFormat={selectedFormat}
            onSelect={handleFormatSelect}
          />

          {/* Format-switch confirmation modal */}
          <Dialog
            open={formatSwitchModal.open}
            onOpenChange={(open) => {
              if (!open) setFormatSwitchModal({ open: false, pendingFormat: null });
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Switch Report Format?</DialogTitle>
                <DialogDescription>
                  Switching format will adjust your canvas layout. Your widgets will be
                  preserved but repositioned for{" "}
                  {formatSwitchModal.pendingFormat === "slide-deck" ? "landscape" : "portrait"}.
                  Continue?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setFormatSwitchModal({ open: false, pendingFormat: null })}
                >
                  Cancel
                </Button>
                <Button onClick={handleFormatSwitchConfirm}>
                  Switch Format
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      );
    }

    // Preview step (Phase 5: combined date range + preview)
    if (selectedTemplate && step === "preview") {
      return (
        <>
          <CompactDateRangeBar
            state={dateRange.state}
            onSelectPreset={dateRange.selectPreset}
            onToggleComparison={dateRange.toggleComparison}
            validationError={validationError}
          />
          <ReportPreviewPanel
            widgets={layoutWidgets}
            comparisonEnabled={dateRange.state.comparisonEnabled}
            dateRange={{
              start: dateRange.state.start,
              end: dateRange.state.end,
              comparisonEnabled: dateRange.state.comparisonEnabled,
              comparisonStart: dateRange.state.comparisonStart,
              comparisonEnd: dateRange.state.comparisonEnd,
            }}
            clientName={client?.name}
            onBack={handleBackToCanvas}
            onFullScreen={() => setShowPreviewModal(true)}
            widgetData={realWidgetData}
            platformStatus={platformStatus}
            dataLoading={dataLoading}
            hasStaleComparison={hasStaleComparison}
            onSave={handleSaveReport}
            isSaving={isSaving}
            onGeneratePdf={savedReportId ? handleGeneratePdf : undefined}
            onDownloadPdf={handleDownloadPdf}
            pdfStatus={pdf.status}
            format={selectedFormat}
          />

          <PdfGenerationModal
            isOpen={showPdfModal}
            status={pdf.status}
            error={pdf.error}
            onRetry={handlePdfRetry}
            onClose={handlePdfModalClose}
          />

          <ReportPreviewModal
            open={showPreviewModal}
            onOpenChange={setShowPreviewModal}
            widgets={layoutWidgets}
            widgetData={previewData}
            format={selectedFormat}
          />

          <Toast toast={toast} />
        </>
      );
    }

    // Canvas step — redirect to the dedicated editor route (Story 5.31). For a
    // roll-up the redirect carries the kind/scope/member set (Story 40-6).
    if (selectedTemplate && step === "canvas") {
      navigate({
        to: "/reports/$reportId/edit",
        params: { reportId: "new" },
        search: buildEditorSearch(templateId),
        replace: true,
      });
      return null;
    }

    // Template gallery (default) — client selector is inline above the gallery
    return (
      <>
        <div className="max-w-5xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-6">
            <h2 className="text-[21px] font-semibold tracking-tight">
              {isRollup ? "Choose a Starting Point" : "Choose Client for Report"}
            </h2>
            <p className="text-[15px] text-foreground/70 mt-1">
              {isRollup
                ? `This roll-up spans ${rollupMemberIds.length} ${rollupMemberIds.length === 1 ? "client" : "clients"}. Pick a template or start from scratch.`
                : "Select the client for this report and the format that best fits your delivery method"}
            </p>
          </div>

          {/* Centered client selector — single-client only; a roll-up's member
              set was chosen on the type step (no per-client selector here). */}
          {!isRollup && (
            <div className="flex flex-col items-center mb-6" data-testid="rollup-client-selector-slot">
              <ClientSelectorPopover
                selectedClientId={clientId}
                selectedClient={client ?? null}
                onSelectClient={(id) => {
                  navigate({
                    to: "/reports/generate",
                    search: { clientId: id },
                  });
                }}
              />
            </div>
          )}

          <AnimatePresence mode="wait">
            {!templatesExpanded ? (
              <motion.div
                key="cards"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
              >
                {/* Forking path from client selector — 3 L-shaped branches, 90° angles, to centers of cards via CSS grid */}
                {selectedFormat === "document" && (() => {
                  // Dim by default; light up only the segments under the hovered card.
                  // Hover highlight is gated on having a selected client.
                  const dimColor = "text-muted-foreground/40";
                  const activeHover = clientId ? hoveredCard : null;
                  const stemColor = activeHover ? "text-primary" : dimColor;
                  // Bus stays dim by default. When hovering "smart", no bus traversal is needed
                  // (the stem comes down to the middle, which is where the smart drop starts).
                  // Scratch/mcp light up half the bus via an overlay layer below.
                  const busColor = dimColor;
                  const dropClass = (slot: "scratch" | "smart" | "mcp") =>
                    `flex justify-center transition-colors duration-200 ${
                      activeHover === slot ? "text-primary" : dimColor
                    }`;
                  // Horizontal bus segment overlay: when "scratch" hovered, fill left half (15.9% → 50%);
                  // when "mcp" hovered, fill right half (50% → 15.9%); when "smart" hovered, no extra
                  // overlay needed (the stem-to-bus intersection is already at center).
                  const overlayStyle: React.CSSProperties | null =
                    activeHover === "scratch"
                      ? { left: "15.9%", right: "50%" }
                      : activeHover === "mcp"
                        ? { left: "50%", right: "15.9%" }
                        : null;
                  return (
                    <div className="-mt-6 mb-1" aria-hidden>
                      {/* Stem (its own SVG so it isn't stretched) */}
                      <div className={`flex justify-center transition-colors duration-200 ${stemColor}`}>
                        <svg width="4" height="36" viewBox="0 0 4 36" className="overflow-visible" style={{ transform: "translateX(-0.5px)" }}>
                          <line x1="2" y1="0" x2="2" y2="36" stroke="currentColor" strokeWidth="1.5" strokeLinecap="butt" />
                        </svg>
                      </div>
                      {/* Drops + horizontal bus — laid out in a 3-col grid that matches the cards grid below (gap-6).
                          Bus is absolutely positioned and only spans from the center of card 1 to the center of card 3. */}
                      <div className="grid grid-cols-3 gap-6 relative">
                        {/* Horizontal bus — base layer (dim or fully red when smart hovered) */}
                        <div
                          className={`absolute top-0 h-[1.5px] rounded-full transition-colors duration-200 ${busColor}`}
                          style={{ left: "15.9%", right: "15.9%", backgroundColor: "currentColor" }}
                        />
                        {/* Bus overlay — only present when hovering scratch or mcp; covers the dim half with red */}
                        {overlayStyle && (
                          <div
                            className="absolute top-0 h-[1.5px] rounded-full bg-current text-primary transition-colors duration-200"
                            style={overlayStyle}
                          />
                        )}
                        {(["scratch", "smart", "mcp"] as const).map((slot) => (
                          <div key={slot} className={dropClass(slot)}>
                            <svg width="14" height="55" viewBox="0 0 14 55" className="overflow-visible">
                              <line x1="7" y1="0" x2="7" y2="52" stroke="currentColor" strokeWidth="1.5" strokeLinecap="butt" />
                              <polyline points="1,47 7,53 13,47" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Start from Scratch + Smart Report + MCP (Smart Report only for document format) */}
                <div className={`grid grid-cols-1 ${selectedFormat === "document" ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6 items-stretch`}>
                  {/* Start from Scratch — enabled once a single client OR a
                      roll-up member set is chosen (Story 40-6). */}
                  <div
                    onMouseEnter={() => setHoveredCard("scratch")}
                    onMouseLeave={() => setHoveredCard(null)}
                    className={`transition-all duration-200 rounded-[30px] ${reportIdentityReady ? "group/card hover:-translate-y-0.5 hover:shadow-sm" : ""}`}
                  >
                    <StartFromScratchCard
                      onStart={handleStartFromScratch}
                      disabled={!reportIdentityReady}
                    />
                  </div>

                  {selectedFormat === "document" && (
                    <>
                      {/* Smart Report */}
                      <div
                        onMouseEnter={() => setHoveredCard("smart")}
                        onMouseLeave={() => setHoveredCard(null)}
                        className={`transition-all duration-200 rounded-[30px] ${clientId ? "group/card hover:-translate-y-0.5 hover:shadow-sm" : ""}`}
                      >
                        <AnimatePresence>
                          {clientId && connectedPlatforms.length > 0 && (
                            <motion.div
                              key="smart-report"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.3, ease: "easeOut" }}
                            >
                              <SmartReportCard
                                connectedPlatforms={connectedPlatforms}
                                onGenerate={handleSmartReport}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {!clientId && (
                          <div className="opacity-50 pointer-events-none">
                            <SmartReportCard
                              connectedPlatforms={["google-analytics-4", "facebook-ads", "instagram-business", "linkedin-pages"]}
                              onGenerate={() => {}}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Create with MCP (coming soon) */}
                  <div
                    onMouseEnter={() => setHoveredCard("mcp")}
                    onMouseLeave={() => setHoveredCard(null)}
                    className={`transition-all duration-200 rounded-[30px] ${clientId ? "group/card hover:-translate-y-0.5 hover:shadow-sm" : ""}`}
                  >
                    <McpReportCard />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="templates-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Missing integrations banner */}
                {clientId && (
                  <AnimatePresence>
                    <MissingIntegrationsBanner
                      visible={dialogState.open}
                      missingPlatforms={dialogState.missingPlatforms}
                      allMissing={dialogState.allMissing}
                      clientId={clientId}
                      onContinue={() => {
                        const tid = dialogState.pendingTemplateId;
                        setDialogState((s) => ({ ...s, open: false }));
                        if (tid) {
                          confirmSelection(tid);
                        }
                      }}
                      onDismiss={() =>
                        setDialogState((s) => ({ ...s, open: false }))
                      }
                    />
                  </AnimatePresence>
                )}

                <TemplateGallery
                  templates={filteredTemplates}
                  connectedPlatforms={connectedPlatforms}
                  isLoading={templatesLoading || integrationsLoading}
                  onSelectTemplate={handleSelectTemplate}
                  onPreviewTemplate={setPreviewTemplateId}
                  clientId={clientId ?? ""}
                  expanded={true}
                  onExpand={() => setTemplatesExpanded(true)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Template preview side panel (Phase 2) */}
        <TemplatePreviewPanel
          template={previewTemplate}
          connectedPlatforms={connectedPlatforms}
          onUseTemplate={handleSelectTemplate}
          onClose={() => setPreviewTemplateId(null)}
        />
      </>
    );
  };

  if (isGenerating) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6">
          <div className="w-14 h-14 border-[6px] border-muted border-t-primary rounded-full animate-spin" />
          <p className="text-base font-medium text-muted-foreground animate-pulse">
            Generating your report...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 max-w-[1800px] mx-auto">
        <ReportBreadcrumb items={breadcrumbItems} onBeforeNavigate={() => setIsExiting(true)} />
        <AnimatePresence mode="wait">
          {!isExiting && (
            <motion.div
              key={contentKey}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {renderStepContent()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </>
  );
}
